class RecentlyViewed extends HTMLElement {
  constructor() {
    super();

    this.mount = this.querySelector('[data-mount]');
    this.previousButton = this.querySelector('[data-previous]');
    this.nextButton = this.querySelector('[data-next]');
    this.controls = this.querySelector('[data-controls]');
    this.scrollbar = this.querySelector('[data-scrollbar]');
    this.scrollbarTrack = this.querySelector('[data-scrollbar-track]');
    this.scrollbarThumb = this.querySelector('[data-scrollbar-thumb]');

    this.maxProducts = Number.parseInt(this.dataset.maxProducts || '8', 10);
    this.storageKey = this.dataset.storageKey || 'fabric-recently-viewed';

    this.slideshow = null;
    this.scroller = null;
    this.scrollbarDragState = null;
    this.resizeObserver = null;
    this.initialised = false;
  }

  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    this.initialise();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  async initialise() {
    const handles = this.getStoredHandles().slice(0, this.maxProducts);
    if (handles.length === 0) return;

    const cards = await Promise.all(
      handles.map((handle) => this.fetchProductCard(handle))
    );

    const validCards = cards.filter(Boolean);

    if (validCards.length === 0) {
      console.warn(
        'Recently viewed: product history exists, but no product cards were returned.'
      );
      return;
    }

    const slideshow = this.buildSlideshow(validCards);

    this.mount.replaceChildren(slideshow);
    this.slideshow = slideshow;
    this.scroller = slideshow.querySelector('slideshow-slides[ref="scroller"]');

    this.hidden = false;
    this.bindEvents();

    requestAnimationFrame(() => {
      this.updateNavigation();
      this.updateScrollbar();
    });
  }

  getStoredHandles() {
    try {
      const value = JSON.parse(localStorage.getItem(this.storageKey) || '[]');

      return Array.isArray(value)
        ? value.filter(
            (item) => typeof item === 'string' && item.trim() !== ''
          )
        : [];
    } catch (error) {
      console.warn('Recently viewed products could not be read.', error);
      return [];
    }
  }

  async fetchProductCard(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}`;

    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        console.warn(`Recently viewed: ${handle} returned HTTP ${response.status}.`);
        return null;
      }

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const responseWrapper = parsedDocument.querySelector(
        '.recently-viewed-card-response'
      );

      if (!responseWrapper || !responseWrapper.innerHTML.trim()) {
        console.warn(
          `Recently viewed: no Viewed card renderer was found for ${handle}.`
        );
        return null;
      }

      this.simplifyProductGallery(responseWrapper);
      return responseWrapper;
    } catch (error) {
      console.warn(`Recently viewed product could not be loaded: ${handle}`, error);
      return null;
    }
  }

  simplifyProductGallery(card) {
    const galleries = card.querySelectorAll('card-gallery, .card-gallery');

    galleries.forEach((gallery) => {
      const gallerySlides = gallery.querySelectorAll('slideshow-slide');

      gallerySlides.forEach((slide, index) => {
        if (index > 0) slide.remove();
      });

      gallery
        .querySelectorAll(
          [
            'slideshow-arrows',
            'slideshow-controls',
            '.slideshow-controls',
            '[data-gallery-navigation]',
          ].join(',')
        )
        .forEach((control) => control.remove());
    });
  }

  buildSlideshow(cards) {
    const slideshow = document.createElement('slideshow-component');
    slideshow.className = 'recently-viewed__slideshow';
    slideshow.setAttribute('initial-slide', '0');

    const container = document.createElement('slideshow-container');
    container.setAttribute('ref', 'slideshowContainer');

    const scroller = document.createElement('slideshow-slides');
    scroller.setAttribute('ref', 'scroller');
    scroller.setAttribute('tabindex', '-1');

    cards.forEach((card, index) => {
      const slide = document.createElement('slideshow-slide');
      slide.className = 'recently-viewed__slide';
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('slide-id', `recently-viewed-${index}`);
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

      const item = document.createElement('div');
      item.className = 'recently-viewed__item';
      item.append(card);

      slide.append(item);
      scroller.append(slide);
    });

    container.append(scroller);
    slideshow.append(container);

    return slideshow;
  }

  bindEvents() {
    this.previousButton?.addEventListener('click', (event) => {
      if (this.slideshow && typeof this.slideshow.previous === 'function') {
        this.slideshow.previous(event);
      } else {
        this.scrollByOneCard(-1);
      }
    });

    this.nextButton?.addEventListener('click', (event) => {
      if (this.slideshow && typeof this.slideshow.next === 'function') {
        this.slideshow.next(event);
      } else {
        this.scrollByOneCard(1);
      }
    });

    this.scroller?.addEventListener(
      'scroll',
      () => {
        this.updateNavigation();
        this.updateScrollbar();
      },
      { passive: true }
    );

    this.scrollbarTrack?.addEventListener('pointerdown', (event) => {
      if (event.target === this.scrollbarThumb) return;
      this.jumpScrollbarToPointer(event);
    });

    this.scrollbarThumb?.addEventListener('pointerdown', (event) => {
      this.startScrollbarDrag(event);
    });

    this.scrollbarThumb?.addEventListener('pointermove', (event) => {
      this.dragScrollbar(event);
    });

    this.scrollbarThumb?.addEventListener('pointerup', (event) => {
      this.stopScrollbarDrag(event);
    });

    this.scrollbarThumb?.addEventListener('pointercancel', (event) => {
      this.stopScrollbarDrag(event);
    });

    this.scrollbarThumb?.addEventListener('lostpointercapture', () => {
      this.stopScrollbarDrag();
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.updateNavigation();
      this.updateScrollbar();
    });

    if (this.scroller) this.resizeObserver.observe(this.scroller);
  }

  getScrollStep() {
    const firstSlide = this.scroller?.querySelector('.recently-viewed__slide');

    if (!firstSlide || !this.scroller) {
      return this.scroller?.clientWidth || 0;
    }

    const styles = window.getComputedStyle(this.scroller);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');

    return firstSlide.getBoundingClientRect().width + gap;
  }

  scrollByOneCard(direction) {
    if (!this.scroller) return;

    this.scroller.scrollBy({
      left: this.getScrollStep() * direction,
      behavior: 'smooth',
    });
  }

  getMaximumScroll() {
    if (!this.scroller) return 0;

    return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
  }

  updateNavigation() {
    if (!this.scroller) return;

    const maximumScroll = this.getMaximumScroll();
    const hasOverflow = maximumScroll > 2;

    if (this.controls) this.controls.hidden = !hasOverflow;
    if (this.previousButton) this.previousButton.disabled = this.scroller.scrollLeft <= 2;
    if (this.nextButton) {
      this.nextButton.disabled = this.scroller.scrollLeft >= maximumScroll - 2;
    }
  }

  updateScrollbar() {
    if (
      !this.scroller ||
      !this.scrollbar ||
      !this.scrollbarTrack ||
      !this.scrollbarThumb
    ) {
      return;
    }

    const maximumScroll = this.getMaximumScroll();
    const hasOverflow = maximumScroll > 2;

    this.scrollbar.hidden = !hasOverflow;
    if (!hasOverflow) return;

    const trackWidth = this.scrollbarTrack.clientWidth;
    const visibleRatio = this.scroller.clientWidth / this.scroller.scrollWidth;
    const thumbWidth = Math.max(48, trackWidth * visibleRatio);
    const availableTravel = Math.max(0, trackWidth - thumbWidth);
    const progress = maximumScroll === 0 ? 0 : this.scroller.scrollLeft / maximumScroll;

    this.scrollbarThumb.style.width = `${thumbWidth}px`;
    this.scrollbarThumb.style.transform = `translateX(${availableTravel * progress}px)`;
  }

  jumpScrollbarToPointer(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    event.preventDefault();

    const rect = this.scrollbarTrack.getBoundingClientRect();
    const thumbWidth = this.scrollbarThumb.getBoundingClientRect().width;
    const availableTravel = Math.max(0, rect.width - thumbWidth);
    const desiredLeft = Math.min(
      availableTravel,
      Math.max(0, event.clientX - rect.left - thumbWidth / 2)
    );
    const progress = availableTravel === 0 ? 0 : desiredLeft / availableTravel;

    this.scroller?.scrollTo({
      left: this.getMaximumScroll() * progress,
      behavior: 'smooth',
    });
  }

  startScrollbarDrag(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    this.scrollbarThumb.setPointerCapture(event.pointerId);
    this.scrollbarDragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.scroller?.scrollLeft || 0,
    };
  }

  dragScrollbar(event) {
    if (
      !this.scrollbarDragState ||
      event.pointerId !== this.scrollbarDragState.pointerId ||
      !this.scroller
    ) {
      return;
    }

    event.preventDefault();

    const trackWidth = this.scrollbarTrack.clientWidth;
    const thumbWidth = this.scrollbarThumb.getBoundingClientRect().width;
    const availableTravel = Math.max(1, trackWidth - thumbWidth);
    const maximumScroll = this.getMaximumScroll();
    const pointerDelta = event.clientX - this.scrollbarDragState.startX;
    const scrollDelta = (pointerDelta / availableTravel) * maximumScroll;

    this.scroller.scrollLeft = this.scrollbarDragState.startScrollLeft + scrollDelta;
  }

  stopScrollbarDrag(event) {
    if (!this.scrollbarDragState) return;

    if (
      event?.pointerId !== undefined &&
      event.pointerId !== this.scrollbarDragState.pointerId
    ) {
      return;
    }

    const pointerId = this.scrollbarDragState.pointerId;

    if (this.scrollbarThumb.hasPointerCapture(pointerId)) {
      this.scrollbarThumb.releasePointerCapture(pointerId);
    }

    this.scrollbarDragState = null;
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}